import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { WpsConfirmView, WpsConfirmWarning } from '../../../../models/payroll';
import { Result } from '../../../../models/general';
import { getApiErrorMessage } from '../../../../utils/apiErrors';

/**
 * The confirm pattern of the WPS routes. Called without confirm the server changes nothing and answers with named
 * warnings; the screen shows them in place and calls again with confirm. A refusal is kept to show beside the action.
 */
export const useWpsConfirm = (onDone: (warnings: WpsConfirmWarning[]) => void) => {
  const { t } = useTranslation();
  const [warnings, setWarnings] = useState<WpsConfirmWarning[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const reset = useCallback(() => {
    setWarnings(null);
    setError(null);
  }, []);

  const run = useCallback(
    async (call: () => Promise<Result<WpsConfirmView>>) => {
      setBusy(true);
      setError(null);
      try {
        const result = await call();
        if (result.errors.length > 0) {
          setError(getApiErrorMessage(result, t));
        } else if (result.payload.done) {
          setWarnings(null);
          onDone(result.payload.warnings ?? []);
        } else {
          setWarnings(result.payload.warnings ?? []);
        }
      } catch (e) {
        setError(getApiErrorMessage(e, t));
      } finally {
        setBusy(false);
      }
    },
    [onDone, t],
  );

  return { warnings, setWarnings, error, setError, busy, setBusy, run, reset };
};
